import { ReactElement, useState } from 'react';
import Layout from '@/shared/Layout/ui/Layout';
import Subscribe from '@/shared/Subscribe/ui/Subscribe';
import * as S from './PartnersDetail.styles';
import { CommonMeta } from '@/common/ui/meta/CommonMeta';
import URL, { buildRootUrl } from '@/common/app/utils/urlsUtils';
import { IBreadcrumbsItem } from '@/shared/Breadcrumbs/domain/Breadcrumbs.domain';
import { TPartner } from '@/common/service/api/Partners/Partners.domain';
import Partners from '@/shared/images/HeroImages/Partners.jpg';
import { Destination } from '@/entities/Attractions/domain/Attractions.domain';
import ReactMarkdown from 'react-markdown';
import { IFullList } from '@/common/service/api/Activity/Activity.domain';
import { ActivitiesList } from '../AttractionsPage/ui/ActivitiesList';
import { IRecommendsItem } from '@/shared/FeaturedActivities/ui/featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { NewFeaturedActivities } from '@/shared/FeaturedActivities/ui/NewFeaturedActivities';
import { useThemeContext } from '@/common/app/contexts';
import { AnswersFAQ } from '../ActivityPage/ui/Answers/AnswersFAQ';

export type TPartnerDetailProps = {
    breadcrumbs: IBreadcrumbsItem[];
    item: TPartner;
    currentDestination: Destination;
    currentPartnerId: number;
    fullList: IFullList;
    placeholders: {
        [key: string]: string;
    };
    recommendedTrips: IRecommendsItem[];
};

const PartnerDetail = ({
    item,
    currentDestination,
    breadcrumbs,
    fullList,
    placeholders,
    recommendedTrips,
}: TPartnerDetailProps): ReactElement => {
    const { theme } = useThemeContext();
    const [readMore, setReadMore] = useState(false);

    const meta = fullList.meta || {
        description: '',
        title: '',
    };
    const items = fullList.items;

    const isLight = theme.name === 'light';
    const item_list_id = typeof window !== 'undefined' ? window.location.pathname : '';
    const isTruncate = item.description && item.description.length > 220;
    const imgSrc = items[0]
        ? `${URL.image}/activity/${items[0].id}/1080x1080/${items[0].slug}.jpg`
        : currentDestination.id !== '0'
        ? `https://images.tripshock.com/destination/${currentDestination.id}/${currentDestination.slug}.jpg`
        : Partners.src;
    const pageUrl = buildRootUrl(item.url);

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <CommonMeta
                {...meta}
                imageSrc={imgSrc}
                slug={meta.canonical || ''}
                ogImageWidth="1080"
                ogImageHeight="1080"
                ogDescription={
                    meta?.description ||
                    "The USA's #1 booking website for activities, tours, and things to do. Browse reviews, deals, photos and more from over 1,000+ experiences. Low price guarantee on every booking."
                }
                ogUrl={pageUrl}
            />
            <S.Container data-test-id="Partners_detail">
                <S.Title>{item.title} Activities</S.Title>
                <S.Description isActive={readMore}>
                    <ReactMarkdown linkTarget="_blank">{item.description}</ReactMarkdown>
                </S.Description>
                {isTruncate && (
                    <S.ReadMore onClick={() => setReadMore(!readMore)} isActive={readMore}>
                        {readMore ? 'See less' : 'Read more'}
                        <S.Arrow isActive={readMore} />
                    </S.ReadMore>
                )}
                {!items.length && (
                    <>
                        <S.EmptyPartnerMessageContainer>
                            <S.EmptyPartnerMessage>
                                Sorry, there are no activities offered by {item.title} on
                                tripshock.com at this time
                            </S.EmptyPartnerMessage>
                        </S.EmptyPartnerMessageContainer>
                        <S.ImageContainer isLight={isLight} />
                    </>
                )}
            </S.Container>
            <ActivitiesList
                title={meta.title}
                defaultList={fullList?.items?.slice(0, 4)}
                listNameGA3={item.title}
                listName={`${item.title} Activities`}
                listId={''}
                item_list_id={item_list_id}
                item_list_name={`Featured ${currentDestination.name} Activities`}
                placeholders={placeholders}
                recommends={[]}
                availableAttributes={[]}
            />
            <NewFeaturedActivities
                defaultActivities={recommendedTrips || []}
                title={`Featured ${currentDestination.name} Activities`}
                item_list_id={item_list_id}
                item_list_name={`Featured ${currentDestination.name} Activities`}
                placeholders={placeholders}
            />
            <S.WrapperSubscribe>
                <Subscribe />
            </S.WrapperSubscribe>
            <AnswersFAQ title={meta.title} url={pageUrl} image={imgSrc} id={`p${item.id}`} />
        </Layout>
    );
};

export { PartnerDetail };
